/**
 * Product Form — AJAX add-to-cart + variant switching
 * Custom element <product-form>
 */

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.variantData = this.getVariantData();
    this.priceEl = this.querySelector('[data-price]');
    this.comparePriceEl = this.querySelector('[data-compare-price]');
    this.submitPriceEl = this.querySelector('[data-submit-price]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.addButtonText = this.querySelector('[data-add-to-cart-text]');
    this.errorEl = this.querySelector('[data-form-error]');
    this.i18n = {
      addToCart: this.dataset.i18nAddToCart || 'Add to cart',
      soldOut: this.dataset.i18nSoldOut || 'Sold out',
      adding: this.dataset.i18nAdding || 'Adding...',
      errorSession: this.dataset.i18nErrorSession || 'Storefront session expired — refresh the page and try again.',
      errorGeneric: this.dataset.i18nErrorGeneric || 'Could not add to cart',
    };

    this.bindEvents();
    this.updateFromUrl();
  }

  bindEvents() {
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    // Listen to hidden selects (swatches/pills update these)
    this.querySelectorAll('select[data-option-index]').forEach(select => {
      select.addEventListener('change', () => this.onVariantChange());
    });
  }

  getVariantData() {
    const el = this.querySelector('[data-product-variants]');
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch { return []; }
  }

  updateFromUrl() {
    const variantId = new URLSearchParams(window.location.search).get('variant');
    if (variantId) {
      const variant = this.variantData.find(v => v.id === parseInt(variantId, 10));
      if (variant) {
        this.setSelectedOptions(variant);
        this.updateDisplay(variant);
      }
    }
  }

  setSelectedOptions(variant) {
    variant.options.forEach((value, index) => {
      const select = this.querySelector(`select[data-option-index="${index}"]`);
      if (select) select.value = value;
      // Update swatches, text-options, or legacy pills
      this.querySelectorAll(`[data-option-group="${index}"] [data-option-value]`).forEach(p => {
        p.classList.toggle('is-selected', p.dataset.optionValue === value);
      });
      // Update selected label
      const label = this.querySelector(`[data-selected-option="${index}"]`);
      if (label) label.textContent = value;
    });
  }

  onVariantChange() {
    const selectedOptions = [];
    this.querySelectorAll('select[data-option-index]').forEach(s => selectedOptions.push(s.value));
    const variant = this.variantData.find(v => v.options.every((opt, i) => opt === selectedOptions[i]));

    if (variant) {
      this.updateDisplay(variant);
      this.updateUrl(variant);
      this.updateHiddenInput(variant);
    }
  }

  updateDisplay(variant) {
    if (this.priceEl) this.priceEl.textContent = this.formatWhole(variant.price);
    if (this.submitPriceEl) this.submitPriceEl.textContent = this.formatWhole(variant.price);

    if (this.comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = this.formatWhole(variant.compare_at_price);
        this.comparePriceEl.style.display = '';
      } else {
        this.comparePriceEl.style.display = 'none';
      }
    }

    if (this.addButton) this.addButton.disabled = !variant.available;
    if (this.addButtonText) {
      this.addButtonText.textContent = variant.available ? this.i18n.addToCart : this.i18n.soldOut;
    }
  }

  formatWhole(cents) {
    return String(Math.round(cents / 100));
  }

  updateUrl(variant) {
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  updateHiddenInput(variant) {
    const input = this.form?.querySelector('input[name="id"]');
    if (input) input.value = variant.id;
  }

  async addToCart() {
    if (this.addButton) this.addButton.disabled = true;
    if (this.addButtonText) this.addButtonText.textContent = this.i18n.adding;
    if (this.errorEl) this.errorEl.textContent = '';

    const formData = new FormData(this.form);

    try {
      const res = await fetch(this.form?.action || '/cart/add', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });

      const contentType = res.headers.get('content-type') || '';
      const isJson = contentType.includes('json') || contentType.includes('javascript');
      const data = isJson ? await res.json().catch(() => null) : null;

      if (!res.ok) {
        const fallback = res.status === 401
          ? this.i18n.errorSession
          : `${this.i18n.errorGeneric} (${res.status})`;
        throw new Error(data?.description || data?.message || fallback);
      }

      document.body.dispatchEvent(new CustomEvent('cart:add', { detail: { item: data }, bubbles: true }));

      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.open();

    } catch (err) {
      if (this.errorEl) this.errorEl.textContent = err.message;
      console.error('Add to cart error:', err);
    } finally {
      if (this.addButton) {
        const input = this.form?.querySelector('input[name="id"]');
        const variant = this.variantData.find(v => v.id === parseInt(input?.value, 10));
        this.addButton.disabled = variant ? !variant.available : false;
      }
      if (this.addButtonText && !this.addButton?.disabled) {
        this.addButtonText.textContent = this.i18n.addToCart;
      }
    }
  }

  formatMoney(cents) {
    return `€${(cents / 100).toFixed(2).replace('.', ',').replace(',00', ',-')}`;
  }
}

customElements.define('product-form', ProductForm);
